import { useState } from 'react';
import { Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCreateComment } from '@/hooks/use-comments';
import { useAuth } from '@/hooks/use-auth';
import { can, useMyCapabilities } from '@/hooks/use-permissions';
import {
  formatRelativeTime,
  formatDateTime,
  formatActivityTimestamp,
  getInitials,
} from '@/lib/utils';
import type { TaskComment, TaskHistoryEntry } from '@/types';

interface TaskCommentsProps {
  taskId: string;
  comments: TaskComment[];
  history?: TaskHistoryEntry[];
}

const PRIORITY_LABELS: Record<string, string> = {
  '0': 'None',
  '1': 'Low',
  '2': 'Medium',
  '3': 'High',
  '4': 'Urgent',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
  none: 'None',
};

function labelForPriority(value: string | number | null | undefined): string {
  if (value == null) return 'None';
  return PRIORITY_LABELS[String(value)] ?? String(value);
}

function truncate(text: string | null | undefined, max = 120): string {
  if (!text) return '(empty)';
  if (text.length <= max) return text;
  return text.slice(0, max) + '…';
}

function formatChangeDetails(
  action: string,
  metadata: Record<string, unknown> | undefined,
): { label: string; lines: string[] }[] | null {
  if (!metadata) return null;

  switch (action) {
    case 'task_status_changed':
      return [
        {
          label: 'Status changed',
          lines: [
            `From: ${metadata.previousStatusName ?? 'Unknown'}`,
            `To: ${metadata.statusName ?? 'Unknown'}`,
          ],
        },
      ];

    case 'task_priority_changed':
      return [
        {
          label: 'Priority changed',
          lines: [
            `From: ${labelForPriority(metadata.previousPriority as string | number | null)}`,
            `To: ${labelForPriority(metadata.priority as string | number | null)}`,
          ],
        },
      ];

    case 'task_due_date_changed': {
      const prev = metadata.previousDueDate
        ? formatDateTime(metadata.previousDueDate as string)
        : 'No due date';
      const next = metadata.dueDate
        ? formatDateTime(metadata.dueDate as string)
        : 'No due date';
      return [{ label: 'Due date changed', lines: [`From: ${prev}`, `To: ${next}`] }];
    }

    case 'task_title_changed':
      return [
        {
          label: 'Title changed',
          lines: [
            `Before: "${truncate(metadata.previousTitle as string)}"`,
            `After: "${truncate(metadata.title as string)}"`,
          ],
        },
      ];

    case 'task_description_changed':
      return [
        {
          label: 'Description changed',
          lines: [
            `Before: "${truncate(metadata.previousDescription as string)}"`,
            `After: "${truncate(metadata.description as string)}"`,
          ],
        },
      ];

    case 'task_visibility_changed':
      return [
        {
          label: 'Visibility changed',
          lines: [
            `From: ${metadata.previousIsClientVisible ? 'Client-visible' : 'Internal'}`,
            `To: ${metadata.isClientVisible ? 'Client-visible' : 'Internal'}`,
          ],
        },
      ];

    case 'task_assignees_updated': {
      const addedNames = (metadata.addedAssigneeNames as string[] | undefined) ?? [];
      const removedNames = (metadata.removedAssigneeNames as string[] | undefined) ?? [];
      const lines: string[] = [];
      if (addedNames.length) lines.push(`Added: ${addedNames.join(', ')}`);
      if (removedNames.length) lines.push(`Removed: ${removedNames.join(', ')}`);
      if (lines.length === 0) lines.push('Assignees updated');
      return [{ label: 'Assignees changed', lines }];
    }

    case 'task_assigned_team':
      return [{ label: 'Team assignment changed', lines: ['Assigned to a team'] }];

    case 'task_unassigned':
      return [{ label: 'Assignment cleared', lines: ['All assignees removed'] }];

    case 'moved_to_list':
    case 'subtask_moved_to_list':
      return [{ label: 'Moved to different list', lines: [] }];

    case 'subtask_created':
      return [
        {
          label: 'Subtask created',
          lines: [`Title: "${truncate(metadata.title as string)}"`],
        },
      ];

    case 'subtask_deleted':
      return [
        {
          label: 'Subtask deleted',
          lines: [`Title: "${truncate(metadata.title as string)}"`],
        },
      ];

    case 'task_tags_changed': {
      const addedTags = (metadata.addedTagNames as string[] | undefined) ?? [];
      const removedTags = (metadata.removedTagNames as string[] | undefined) ?? [];
      const lines: string[] = [];
      if (addedTags.length) lines.push(`Added: ${addedTags.join(', ')}`);
      if (removedTags.length) lines.push(`Removed: ${removedTags.join(', ')}`);
      if (lines.length === 0) lines.push('Tags updated');
      return [{ label: 'Tags changed', lines }];
    }

    case 'task_custom_field_changed': {
      const fieldName = (metadata.fieldName as string) ?? 'Unknown';
      const prev = metadata.previousValue;
      const next = metadata.newValue;
      const fmtValue = (v: unknown): string => {
        if (v === null || v === undefined) return '(empty)';
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        return truncate(String(v), 80);
      };
      return [
        {
          label: `Custom field changed`,
          lines: [
            `Field: ${fieldName}`,
            `From: ${fmtValue(prev)}`,
            `To: ${fmtValue(next)}`,
          ],
        },
      ];
    }

    case 'task_attachment_added':
      return [
        {
          label: 'Attachment added',
          lines: [`Added attachment: ${metadata.fileName ?? 'file'}`],
        },
      ];

    case 'task_attachment_removed':
      return [
        {
          label: 'Attachment removed',
          lines: [`Removed attachment: ${metadata.fileName ?? 'file'}`],
        },
      ];

    default:
      return null;
  }
}

function hasDetails(action: string, metadata?: Record<string, unknown>): boolean {
  if (!metadata) return false;
  return formatChangeDetails(action, metadata) !== null;
}

function HistoryEntry({
  entry,
  canViewAudit,
}: {
  entry: TaskHistoryEntry;
  canViewAudit: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const showToggle = canViewAudit && hasDetails(entry.action, entry.metadata);
  const details = expanded ? formatChangeDetails(entry.action, entry.metadata) : null;

  return (
    <div className="flex gap-3">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarImage src={entry.actor?.avatarUrl || undefined} />
        <AvatarFallback className="text-[10px] bg-primary/20">
          {entry.actor ? getInitials(entry.actor.displayName) : '•'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium">{entry.actor?.displayName ?? 'System'}</span>{' '}
          <span className="text-muted-foreground">{entry.message}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatActivityTimestamp(entry.createdAt)}
        </p>

        {showToggle && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 flex items-center gap-1 text-xs text-primary/80 hover:text-primary transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" /> Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" /> Show more
              </>
            )}
          </button>
        )}

        {expanded && details && (
          <div className="mt-2 rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-xs space-y-2">
            {details.map((section, i) => (
              <div key={i}>
                <p className="font-medium text-foreground/80">{section.label}</p>
                {section.lines.map((line, j) => (
                  <p key={j} className="text-muted-foreground ml-2">
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TaskComments({ taskId, comments, history = [] }: TaskCommentsProps) {
  const { user } = useAuth();
  const { data: capabilities } = useMyCapabilities();
  const canViewAudit = can(capabilities, 'audit.view');
  const [newComment, setNewComment] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const addComment = useCreateComment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addComment.mutate(
        { taskId, data: { content: newComment.trim() } },
        {
          onSuccess: () => {
            setNewComment('');
          },
        },
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newComment.trim()) {
        addComment.mutate(
          { taskId, data: { content: newComment.trim() } },
          {
            onSuccess: () => {
              setNewComment('');
            },
          },
        );
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* ClickUp-style comment input */}
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={user?.avatarUrl || undefined} />
          <AvatarFallback className="text-xs bg-primary/20">
            {user ? getInitials(user.displayName) : '?'}
          </AvatarFallback>
        </Avatar>
        <form onSubmit={handleSubmit} className="flex-1">
          <div className={`rounded-lg border transition-colors ${isFocused ? 'border-primary bg-[#2a2a2a]' : 'border-[#444] bg-[#1a1a1a]'}`}>
            <textarea
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
              rows={2}
              className="w-full bg-transparent border-0 px-3 py-2 text-sm resize-none focus:outline-none placeholder:text-muted-foreground"
            />
            {/* Action bar */}
            <div className="flex items-center justify-between px-2 pb-2">
              <div />
              <Button
                type="submit"
                size="icon"
                disabled={!newComment.trim() || addComment.isPending}
                className="h-7 w-7"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Task history */}
      {history.length > 0 && (
        <div className="space-y-4">
          {history.map((entry) => (
            <HistoryEntry key={entry.id} entry={entry} canViewAudit={canViewAudit} />
          ))}
        </div>
      )}

      {/* Activity / Comments list */}
      {comments.length > 0 && (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 group">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={comment.author.avatarUrl || undefined} />
                <AvatarFallback className="text-[10px] bg-primary/20">
                  {getInitials(comment.author.displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{comment.author.displayName}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(comment.createdAt)}
                  </span>
                  {comment.editedAt && (
                    <span className="text-xs text-muted-foreground italic">(edited)</span>
                  )}
                </div>
                <p className="mt-1 text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {comments.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No comments yet
        </p>
      )}
    </div>
  );
}
